export const SALES_DESK_PERFORMANCE_WINDOWS = [30, 60, 90, 365] as const;
export type SalesDeskPerformanceWindow = (typeof SALES_DESK_PERFORMANCE_WINDOWS)[number];

export type SalesDeskPerformanceRow = {
  id: string;
  customer: string;
  model: string;
  serial: string | null;
  office: string;
  salesperson: string;
  saleStatus: string;
  fulfillment: string;
  createdAt: string;
  updatedAt: string;
  onLot: boolean;
  closed: boolean;
};

export type SalesDeskPerformanceSelection = {
  office: string;
  salesperson: string;
  model: string;
  days: number;
  now: number;
};

export type SalesDeskPerformanceView = {
  received: number;
  closed: number;
  open: number;
  onLot: number;
  meanDaysOpen: number | null;
  table: SalesDeskPerformanceRow[];
};

const DAY_MS = 86_400_000;

export function salesDeskPerformanceCutoff(now: number, days: number): number {
  return now - days * DAY_MS;
}

export function salesDeskSaleIsClosed(status: string): boolean {
  return status.trim().toLowerCase() === "closed";
}

export function salesDeskDaysOpen(createdAt: string, now: number): number | null {
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created) || created > now) return null;
  return Math.floor((now - created) / DAY_MS);
}

export function salesDeskPerformanceChoices(rows: SalesDeskPerformanceRow[]): {
  offices: string[];
  people: string[];
  models: string[];
} {
  const collect = (values: string[]) =>
    [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  return {
    offices: collect(rows.map((row) => row.office)),
    people: collect(rows.map((row) => row.salesperson)),
    models: collect(rows.map((row) => row.model)),
  };
}

function inWindow(iso: string, now: number, days: number): boolean {
  const time = Date.parse(iso);
  return Number.isFinite(time) && time >= salesDeskPerformanceCutoff(now, days) && time <= now;
}

function matchesFacet(row: SalesDeskPerformanceRow, selection: SalesDeskPerformanceSelection): boolean {
  if (selection.office && row.office !== selection.office) return false;
  if (selection.salesperson && row.salesperson !== selection.salesperson) return false;
  if (selection.model && row.model !== selection.model) return false;
  return true;
}

export function salesDeskMeanDaysOpen(rows: Array<{ createdAt: string }>, now: number): number | null {
  const days = rows.map((row) => salesDeskDaysOpen(row.createdAt, now)).filter((day): day is number => day != null);
  if (days.length === 0) return null;
  return Math.round((days.reduce((sum, day) => sum + day, 0) / days.length) * 10) / 10;
}

export function selectSalesDeskPerformance(
  rows: SalesDeskPerformanceRow[],
  selection: SalesDeskPerformanceSelection,
): SalesDeskPerformanceView {
  const matched = rows.filter((row) => matchesFacet(row, selection));
  const received = matched.filter((row) => inWindow(row.createdAt, selection.now, selection.days));
  const closed = matched.filter((row) => row.closed && inWindow(row.updatedAt, selection.now, selection.days));
  const open = matched.filter((row) => !row.closed);
  const onLot = matched.filter((row) => row.onLot);
  const tableIds = new Set<string>();
  const table: SalesDeskPerformanceRow[] = [];
  for (const row of matched) {
    const active = inWindow(row.createdAt, selection.now, selection.days) || inWindow(row.updatedAt, selection.now, selection.days);
    if (!active && !row.onLot) continue;
    if (tableIds.has(row.id)) continue;
    tableIds.add(row.id);
    table.push(row);
  }
  return {
    received: received.length,
    closed: closed.length,
    open: open.length,
    onLot: onLot.length,
    meanDaysOpen: salesDeskMeanDaysOpen(onLot, selection.now),
    table,
  };
}
